// The fleet requirement calculator (TX-US-0030, TX-US-0032): the shared routes, what each needs,
// and the totals. The figures are the API's; nothing here calculates.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { addFleetRoute, listFleetRoutes } from '../../api.js';
import Icon from '../../ui/Icon.jsx';
import DeleteRouteDialog from './DeleteRouteDialog.jsx';
import RouteRow from './RouteRow.jsx';
import {
  DAY_FIELDS, TEXT_FIELDS, UNIT_FIELD, VOLUME_FIELDS, figure, nextRouteName, targetTotals,
} from './routeFields.js';

const RESULT_HEADERS = [
  'Trips per truck / month', 'Trips required / month', 'Trucks required', 'Loads / day',
];

const plain = (label) => <th key={label} className="plain">{label}</th>;
const derived = (label) => <th key={label} className="plain derived num">{label}</th>;

export default function FleetCalculator() {
  const [fleet, setFleet] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(
    () => listFleetRoutes().then(setFleet).catch((err) => setError(err.message)),
    [],
  );

  useEffect(() => { load(); }, [load]);

  const onAdd = async () => {
    setError(null);
    try {
      setFleet(await addFleetRoute({ name: nextRouteName(fleet.routes) }));
    } catch (err) {
      setError(err.message);
    }
  };

  const onDeleted = (next) => {
    setFleet(next);
    setDeleting(null);
  };

  const onGone = () => {
    setDeleting(null);
    load();
  };

  if (!fleet) return <p className={error ? 'error' : 'empty'}>{error ?? 'Loading…'}</p>;

  const { routes, totals, days_per_month: daysPerMonth } = fleet;

  return (
    <section className="econ fleet">
      <header className="register-header econ-header">
        <div>
          <p className="econ-crumb"><Link to="/economics">Economics</Link></p>
          <h2>Fleet requirement calculator</h2>
        </div>
        <div className="register-actions">
          <button type="button" className="icon-button primary" onClick={onAdd}
                  aria-label="Add route" title="Add route">
            <Icon name="plus" />
          </button>
        </div>
      </header>
      <p className="hint">
        One route a row, shared by everyone in Economics. Change a time, a truck size or a target
        and the cycle, the trips and the trucks follow, on a {daysPerMonth}-day month. The question
        mark beside a figure shows how it was reached.
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      {routes.length === 0 ? (
        <p className="empty">No routes yet. Add the first one.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table fleet-table">
            <thead>
              <tr>
                {TEXT_FIELDS.map(({ label }) => plain(label))}
                {DAY_FIELDS.map(({ label }) => plain(label))}
                {derived('Total cycle (days)')}
                {VOLUME_FIELDS.map(({ label }) => plain(label))}
                {plain(UNIT_FIELD.label)}
                {RESULT_HEADERS.map(derived)}
                <th className="plain" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {routes.map((line) => (
                <RouteRow
                  key={line.route.route_id}
                  line={line}
                  daysPerMonth={daysPerMonth}
                  onFleet={setFleet}
                  onGone={onGone}
                  onDelete={setDeleting}
                />
              ))}
            </tbody>
            <tfoot>
              {/* Under the text, day, cycle and capacity columns, so each total sits under its own. */}
              <tr className="fleet-totals">
                <th scope="row" colSpan={TEXT_FIELDS.length + DAY_FIELDS.length + 2}>Total</th>
                {/* One figure a unit, across the target and unit columns. */}
                <td className="fleet-targets" colSpan={2}>{targetTotals(totals.monthly_targets)}</td>
                <td />
                <td className="num">{figure(totals.trips_required)}</td>
                <td className="num fleet-trucks">{figure(totals.trucks_required)}</td>
                <td className="num">{figure(totals.loads_per_day, 2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {deleting && (
        <DeleteRouteDialog
          route={deleting}
          onClose={() => setDeleting(null)}
          onFleet={onDeleted}
          onGone={onGone}
        />
      )}
    </section>
  );
}
